import { useCallback, useRef } from 'react';

interface ViewabilityConfig {
  threshold?: number;
  minViewTime?: number;
  resetKey?: string;
}

export function useViewability(
  onDisplay: () => void,
  onView: () => void,
  config: ViewabilityConfig = {},
) {
  const { threshold = 0.5, minViewTime = 1000, resetKey } = config;
  const displayFiredRef = useRef(false);
  const viewFiredRef = useRef(false);
  const viewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const previousResetKeyRef = useRef<string | undefined>(resetKey);
  const onDisplayRef = useRef(onDisplay);
  const onViewRef = useRef(onView);
  onDisplayRef.current = onDisplay;
  onViewRef.current = onView;

  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
        viewTimerRef.current = null;
      }

      if (resetKey !== previousResetKeyRef.current) {
        displayFiredRef.current = false;
        viewFiredRef.current = false;
        previousResetKeyRef.current = resetKey;
      }

      if (!node) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !document.hidden) {
              if (!displayFiredRef.current) {
                onDisplayRef.current();
                displayFiredRef.current = true;
              }

              if (
                !viewFiredRef.current &&
                !viewTimerRef.current &&
                entry.intersectionRatio >= threshold
              ) {
                viewTimerRef.current = setTimeout(() => {
                  if (!viewFiredRef.current && !document.hidden) {
                    onViewRef.current();
                    viewFiredRef.current = true;
                  }
                  viewTimerRef.current = null;
                }, minViewTime);
              }
            } else {
              if (viewTimerRef.current) {
                clearTimeout(viewTimerRef.current);
                viewTimerRef.current = null;
              }
            }
          });
        },
        { threshold: [0, threshold] },
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [threshold, minViewTime, resetKey],
  );

  return callbackRef;
}
